// src/contexts/CatalogContext.tsx

import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { CatalogItemOptionValue, CatalogObject } from "square";
import { byCategoryOrder } from "../extensions/helpers";
import {
  getLocalStorageExpiryTime,
  getRelativeTTL,
  useLocalStorage,
} from "./useLocalStorage";
import { Option } from "../types/Option";
import { SquareFilterType } from "../types/SquareFilterType";
import { getAll } from "../api";
import { useSiteConfig } from "./SiteConfigContext";

interface CatalogContextType {
  categories: CatalogObject[];
  setCategories: React.Dispatch<React.SetStateAction<CatalogObject[]>>;
  getCategoryById: (categoryId: string) => CatalogObject | undefined;
  getCategoryByName: (name: string) => CatalogObject | undefined;
  getCategoriesByItem: (itemId: string) => CatalogObject[];
  getLowestCategoryByItem: (itemId: string) => CatalogObject | undefined;
  getChildCategories: (categoryId: string) => CatalogObject[];
  getDomains: () => CatalogObject[];
  isTheme: (categoryId: string) => boolean | null | undefined;
  /**
   * True while the nav's categories are unavailable and still on their way —
   * i.e. local storage hasn't hydrated yet, or it hydrated empty and the
   * CATEGORY fetch is in flight. Drives the header's placeholder tabs so the
   * shop tab's slot is visibly held instead of popping in later. Goes false on
   * a failed fetch too, so a Square outage doesn't shimmer forever.
   */
  isCategoriesLoading: boolean;

  options: CatalogObject[];
  setOptions: React.Dispatch<React.SetStateAction<CatalogObject[]>>;
  getOptionByType: (option: Option) => CatalogObject | undefined;
  getOptionValuesByType: (
    option: Option,
  ) => (CatalogItemOptionValue | undefined)[] | undefined;
  colorOption: CatalogObject | undefined;
  sizeOption: CatalogObject | undefined;
  getOptionDataByColor: (
    itemOptionValueId: string,
  ) => CatalogObject | undefined;
  getOptionDataBySize: (itemOptionValueId: string) => CatalogObject | undefined;

  items: CatalogObject[];
  setItems: React.Dispatch<React.SetStateAction<CatalogObject[]>>;
  addItems: (items: CatalogObject[]) => void;
  getItemByName: (name: string) => CatalogObject | undefined;
  getItemsByCategory: (categoryId: string) => CatalogObject[];
  getItemById: (id: string) => CatalogObject | undefined;
  getItemsByIds: (ids: string[]) => CatalogObject[];
  getVariationBySku: (
    sku: string,
  ) => { item: CatalogObject; variation: CatalogObject } | undefined;

  images: CatalogObject[];
  addImages: (images: CatalogObject[]) => void;
  getImagesByIds: (ids: string[]) => CatalogObject[];
  getImagesByItem: (itemId: string) => CatalogObject[];

  /**
   * Drop THIS tab's four catalog caches and refetch. The admin portal calls it
   * straight after bumping the server `catalogEpoch` so the admin's own tab
   * doesn't sit on the pre-bump catalog (and re-write it to localStorage) while
   * every other visitor gets the fresh one.
   */
  bustLocalCatalogCache: (serverEpoch?: number) => void;
}

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

// Merge and keep unique items by ID (favoring last occurrence)
const mergeObjects = (
  list1: CatalogObject[],
  list2: CatalogObject[],
): CatalogObject[] =>
  Array.from(
    new Map([...list1, ...list2].map((item) => [item.id, item])).values(),
  );

export const CatalogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Both TTLs are computed ONCE per mount, not per render. `getRelativeTTL(2)`
  // in particular returns a new timestamp every time it's called, and a moving
  // expiry used to push the stored TTL forward for as long as a tab stayed
  // open — so the "2 hour" items cache never actually expired in a long-lived
  // tab (the admin portal, typically).
  const expiry = useMemo(() => getLocalStorageExpiryTime(21, 0, 0, 0), []); // categories, options, images reset at 9pm
  // Items use a shorter TTL so removed products stop appearing within ~2 hours
  const itemExpiry = useMemo(() => getRelativeTTL(2), []);

  const [categories, setCategories, isCategoriesLoaded] = useLocalStorage<
    CatalogObject[]
  >("americana-company-categories-override", [], expiry);
  const [options, setOptions, isOptionsLoaded] = useLocalStorage<
    CatalogObject[]
  >(
    // Item options hold the colour-swatch hexes rendered by BubbleSelector, so
    // a swatch correction in Square is invisible to returning visitors until
    // this cache drops. Bump the version (v1 → v2 → …) to force a refetch —
    // the items-vN key below does NOT cover option values.
    "americana-company-options-v4",
    [],
    expiry,
  );
  const [items, setItems, isItemsLoaded] = useLocalStorage<CatalogObject[]>(
    // Bump this version (v2 → v3 → …) to force every visitor to drop their
    // cached catalog items and refetch fresh — e.g. after a price change.
    "americana-company-items-v13",
    [],
    itemExpiry,
  );
  // Whether the standalone CATEGORY request below is in flight. Combined with
  // the hydration flag into `isCategoriesLoading` for consumers.
  const [isCategoriesFetching, setIsCategoriesFetching] = React.useState(false);
  const [images, setImages] = useLocalStorage<CatalogObject[]>(
    // Versioned like items/options so a bad or incomplete image cache can be
    // dropped for every visitor without waiting for the 9pm expiry. These
    // objects carry `imageData.name`, which is what orders a colour's photos
    // (front before back) — so a stale entry doesn't just show the wrong
    // picture, it can show the right pictures in the wrong ORDER.
    "americana-company-images-v2",
    [],
    expiry,
  );

  // Instant cache bust for ALL visitors, no deploy. The admin bumps the server
  // `catalogEpoch` (Product Management → Site Controls); when a visitor's stored
  // epoch is behind the server's, we drop the four catalog caches so the fetch
  // effect below refetches everything. No expiry: this marker persists until the
  // next bump. See SiteConfigContext + netlify/functions/admin-site-config.js.
  //
  // IMAGES are cleared here too. They were omitted originally, which made the
  // button unable to fix the one thing owners actually reach for it to fix — a
  // visitor stuck on wrong/missing product photos — because the image cache
  // survived the bust and only rolled over at 9pm.
  const { catalogEpoch, loaded: isSiteConfigLoaded } = useSiteConfig();
  const [cacheEpoch, setCacheEpoch, isCacheEpochLoaded] =
    useLocalStorage<number>("americana-company-epoch", 0);

  const clearCatalogCaches = useCallback(() => {
    setItems([]);
    setOptions([]);
    setCategories([]);
    setImages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSiteConfigLoaded || !isCacheEpochLoaded) return;
    if (catalogEpoch == null) return;
    if (catalogEpoch > cacheEpoch) {
      clearCatalogCaches();
      setCacheEpoch(catalogEpoch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSiteConfigLoaded, isCacheEpochLoaded, catalogEpoch, cacheEpoch]);

  // Local half of the admin "refresh cache for everyone" button. The server
  // epoch bump only reaches a tab on its next site-config load, so without this
  // the tab that pressed the button is the one most likely to be stale.
  const bustLocalCatalogCache = useCallback(
    (serverEpoch?: number) => {
      clearCatalogCaches();
      // Stamp the SERVER's new epoch, never a locally-invented one: a local
      // counter running ahead would swallow the next real bump (the effect
      // above fires only on `server > stored`). With no epoch supplied the
      // marker is left alone — the worst case is one redundant bust on this
      // tab's next load.
      if (typeof serverEpoch === "number") {
        setCacheEpoch((prev) => (serverEpoch > prev ? serverEpoch : prev));
      }
    },
    // `setCacheEpoch` is a plain useState setter (see useLocalStorage), so it is
    // referentially stable and listing it does NOT re-create this callback.
    [clearCatalogCaches, setCacheEpoch],
  );

  const getCategoryById = (categoryId: string) => {
    return categories?.find((c) => c.id === categoryId);
  };

  const getItemById = (id: string) => {
    return items?.find((item) => item.id === id);
  };

  const getCategoryByName = (name: string) => {
    return categories?.find(
      (c) => c.categoryData?.name?.toLowerCase() === name.toLowerCase(),
    );
  };

  const getCategoriesByItem = (itemId: string) => {
    const item = getItemById(itemId);
    return categories?.filter((c) =>
      item?.itemData?.categories?.map((ic) => ic.id)?.includes(c.id),
    );
  };

  const getLowestCategoryByItem = (itemId: string) => {
    return getCategoriesByItem(itemId).find((c) => !c.categoryData?.isTopLevel);
  };

  // Both return the owner's dashboard ordering (Square records a child
  // category's position on `parentCategory.ordinal`). Sorting here rather than
  // at each call site means the nav dropdown, the shop pages and anything else
  // listing categories all agree, and the dashboard is the only place that
  // decides. See byCategoryOrder in src/extensions/helpers.ts.
  const getChildCategories = (categoryId: string) => {
    return byCategoryOrder(
      categories?.filter(
        (c) => c.categoryData?.parentCategory?.id === categoryId,
      ) ?? [],
    );
  };

  const getDomains = () => {
    return byCategoryOrder(
      categories?.filter((c) => c.categoryData?.isTopLevel) ?? [],
    );
  };

  const isTheme = (categoryId: string) => {
    return (
      categories?.find((c) => c.id === categoryId)?.categoryData?.isTopLevel &&
      getChildCategories(categoryId)?.length === 0
    );
  };

  const getOptionByType = useCallback(
    (option: Option) => {
      return options?.find((o) =>
        o.itemOptionData?.name
          ?.toLowerCase()
          .includes(option.toString().toLowerCase()),
      );
    },
    [options],
  );

  const getOptionValuesByType = (option: Option) => {
    return getOptionByType(option)?.itemOptionData?.values?.map(
      (v) => v.itemOptionValueData,
    );
  };

  // Specific Options
  const colorOption = useMemo(
    () => getOptionByType(Option.Color),
    [getOptionByType],
  );
  const sizeOption = useMemo(
    () => getOptionByType(Option.Size),
    [getOptionByType],
  );

  // Given a Color, get Option Data
  const getOptionDataByColor = (itemOptionValueId: string) => {
    return colorOption?.itemOptionData?.values?.find(
      (v) => v.id === itemOptionValueId,
    );
  };

  // Given a Color, get Option Data
  const getOptionDataBySize = (itemOptionValueId: string) => {
    return sizeOption?.itemOptionData?.values?.find(
      (v) => v.id === itemOptionValueId,
    );
  };

  const addItems = (items: CatalogObject[]) => {
    setItems((prevItems) => mergeObjects(prevItems, items));
  };

  const getItemByName = (name: string) => {
    return items?.find(
      (i) => i.itemData?.name?.toLowerCase() === name.toLowerCase(),
    );
  };

  const getItemsByCategory = (categoryId: string) => {
    return items?.filter((i) =>
      i.itemData?.categories?.map((c) => c.id)?.includes(categoryId),
    );
  };

  const getItemsByIds = (ids: string[]) => {
    return items?.filter((i) => ids.includes(i.id));
  };

  /**
   * Resolve a catalog VARIATION SKU to its item + variation. This is the join
   * used by deep-link carts (/payment/checkout?products=…) and by the Meta shop
   * feed, whose content ids are these SKUs — see lib/checkout-link.ts. Matching
   * is case-insensitive because the SKU makes a round trip through Meta.
   */
  const getVariationBySku = (sku: string) => {
    const target = sku.trim().toLowerCase();
    if (!target) return undefined;
    for (const item of items ?? []) {
      const variation = item.itemData?.variations?.find(
        (v) => v.itemVariationData?.sku?.trim().toLowerCase() === target,
      );
      if (variation) return { item, variation };
    }
    // Fallback: a variation with no SKU is exported to the Meta feed under its
    // Square variation id, so accept that as a content id too. Checked second
    // because the SKU is the intended key; id and SKU formats can't collide.
    for (const item of items ?? []) {
      const variation = item.itemData?.variations?.find(
        (v) => v.id.toLowerCase() === target,
      );
      if (variation) return { item, variation };
    }
    return undefined;
  };

  const addImages = (images: CatalogObject[]) => {
    setImages((prevItems) => mergeObjects(prevItems, images));
  };

  const getImagesByIds = (ids: string[]) => {
    return ids?.length > 0 ? images.filter((i) => ids.includes(i.id)) : [];
  };

  const getImagesByItem = (itemId: string) => {
    const item = getItemById(itemId);
    return images.filter((i) => item?.itemData?.imageIds?.includes(i.id));
  };

  // Categories are fetched on their own, ahead of and independently of the
  // items. They're a handful of small objects and they're what the header's
  // shop tabs are built from (TabDropdownList), whereas ITEM is the bulk of
  // the catalog — every product, variation and option — and get-catalog
  // paginates it over several listCatalog calls. Bundling them into one
  // request, as this used to, meant the nav had no shop tab until the entire
  // product payload had downloaded and parsed. Splitting them costs one extra
  // function invocation on a cold visitor and buys a nav that renders with the
  // header.
  useEffect(() => {
    // Need LocalStorage to be loaded to see if data fetching is required
    if (!isCategoriesLoaded) return;
    if (categories.length > 0) return;

    let cancelled = false;
    const retrieveCategories = async () => {
      setIsCategoriesFetching(true);
      try {
        const response = await getAll(SquareFilterType.CATEGORY);
        const data = response?.data;
        if (!cancelled && !!data && !response?.error) {
          setCategories(
            data.filter(
              (obj) => obj.type === SquareFilterType.CATEGORY.toString(),
            ),
          );
        }
      } catch (error) {
        console.error("Get Catalog categories failed: ", error);
      } finally {
        if (!cancelled) setIsCategoriesFetching(false);
      }
    };

    retrieveCategories();
    return () => {
      cancelled = true;
    };

    // categories.length is a dependency so that a cache bust (which empties it)
    // re-triggers the fetch. Once refilled it's non-empty and the guard above
    // makes this a no-op, so there's no refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategoriesLoaded, categories.length]);

  useEffect(() => {
    const retrieveItems = async () => {
      // Types to get
      let types = [];
      if (items?.length === 0) types.push(SquareFilterType.ITEM);
      if (options?.length === 0) types.push(SquareFilterType.ITEM_OPTION);

      // Only need to get any types if we don't have them
      if (types.length > 0) {
        try {
          // 1. Get Catalog
          const dataResponse = await getAll(types.join(","));

          // Data
          const typeData = dataResponse?.data;
          if (dataResponse && !!typeData && !dataResponse.error) {
            const dataItems = typeData.filter(
              (obj) => obj.type === SquareFilterType.ITEM.toString(),
            );
            const dataOptions = typeData.filter(
              (obj) => obj.type === SquareFilterType.ITEM_OPTION.toString(),
            );
            // const variations = itemsResponse.data.filter(obj => obj.type === 'ITEM_VARIATION');

            if (items?.length === 0) setItems(dataItems);
            if (options?.length === 0) setOptions(dataOptions);
          }
        } catch (error) {
          console.error("Get Catalog failed: ", error);
        }
      }
    };

    // Need LocalStorage to be loaded to see if data fetching is required
    if (isOptionsLoaded && isItemsLoaded) {
      if (items.length === 0 || options.length === 0) {
        retrieveItems();
      }
    }

    // The array lengths are dependencies so that a cache bust (which empties
    // them) re-triggers the fetch. Once refilled they're non-empty and the
    // inner guard makes this a no-op, so there's no refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoaded, isItemsLoaded, items.length, options.length]);

  return (
    <CatalogContext.Provider
      value={{
        categories,
        setCategories,
        getCategoryById,
        getCategoryByName,
        getCategoriesByItem,
        getLowestCategoryByItem,
        getChildCategories,
        getDomains,
        isTheme,
        isCategoriesLoading:
          categories.length === 0 &&
          (!isCategoriesLoaded || isCategoriesFetching),
        options,
        setOptions,
        getOptionByType,
        getOptionValuesByType,
        colorOption,
        sizeOption,
        getOptionDataBySize,
        getOptionDataByColor,
        items,
        setItems,
        addItems,
        getItemByName,
        getItemsByCategory,
        getItemsByIds,
        getItemById,
        getVariationBySku,
        images,
        addImages,
        getImagesByItem,
        getImagesByIds,
        bustLocalCatalogCache,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const context = React.useContext(CatalogContext);
  if (context === undefined) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return context;
};
